import React from 'react';
import { TopBar } from '../../components/layout/TopBar';
import { Header } from '../../components/layout/Header';
import { Navigation } from '../../components/layout/Navigation';
import { MobileStickyBar } from '../../components/layout/MobileStickyBar';
import { Footer } from '../../components/layout/Footer';
import { useScreenInit } from '../../useScreenInit';
import { usePageTitle } from '../../hooks/usePageTitle';
import { RotateCcw, AlertTriangle, Sparkles, XCircle, Truck, ShieldCheck, Lock, Phone, Mail, CheckCircle2 } from 'lucide-react';
export function OurPolicies() {
  usePageTitle('Our Policies | MidAtlantic Mailboxes');
  useScreenInit();
  return <div className="min-h-screen flex flex-col bg-brand-background" data-id="element-1564" data-mp="2xzg195wlhfwm">
      <TopBar data-id="element-1565" data-mp="2xxd4m30xsoix" />
      <Header data-id="element-1566" data-mp="2xx3fsirmonet" />
      <Navigation data-id="element-1567" data-mp="8ae3ukc1z975" />

      <main className="flex-grow" data-id="element-1568" data-mp="8axb9742uf6x">
        {/* Hero */}
        <section className="bg-white border-b border-brand-border py-16 lg:py-24" data-id="element-1569" data-mp="8cgrixpqgq8p">
          <div className="max-w-7xl mx-auto px-4 text-center" data-id="element-1570" data-mp="d7e2wbahjbor">
            <span className="inline-block py-1 px-3 rounded-full bg-brand-goldSoft text-brand-charcoal text-xs font-bold tracking-wide mb-4 uppercase border border-brand-gold/30" data-id="element-1571" data-mp="143ldvpc3hkt">
              Customer Service
            </span>
            <h1 className="text-4xl lg:text-5xl xl:text-6xl font-serif font-bold text-brand-charcoal mb-6 leading-tight" data-id="element-1572" data-mp="3o81b8nwtvs7z">
              Our Policies
            </h1>
            <p className="text-lg lg:text-xl text-brand-muted max-w-3xl mx-auto leading-relaxed" data-id="element-1573" data-mp="3ecq4osvkprbb">
              Review our return, shipping, damage, warranty, and privacy
              policies to understand how we handle your orders, claims, and
              information.
            </p>
          </div>
        </section>

        {/* Return Authorization Callout */}
        <section className="py-8 bg-brand-background" data-id="element-1574" data-mp="2vkxch0obwa4n">
          <div className="max-w-5xl mx-auto px-4" data-id="element-1575" data-mp="2vknheyoffcs3">
            <div className="bg-brand-goldWash border border-brand-gold/30 rounded-xl p-6 flex flex-col md:flex-row gap-5 items-start" data-id="element-1576" data-mp="2qlkebjs0bgmr">
              <div className="w-12 h-12 rounded-full bg-white text-brand-maroon flex items-center justify-center shrink-0 shadow-sm" data-id="element-1577" data-mp="2gxosbad1kugj">
                <AlertTriangle size={24} data-id="element-1578" data-mp="2lv8a2k8y7pd8" />
              </div>
              <div data-id="element-1579" data-mp="271ls4a1r35w8">
                <h2 className="text-lg font-bold text-brand-charcoal mb-2" data-id="element-1580" data-mp="272547a4pz02c">
                  A Return Authorization (RGA#) Is Required for All Returns
                </h2>
                <p className="text-brand-charcoal/80 text-sm leading-relaxed" data-id="element-1581" data-mp="2zqandsg4kh02">
                  Before sending anything back, contact our customer service
                  offices at{' '}
                  <a href="tel:240-575-5852" className="font-semibold text-brand-maroon hover:underline" data-id="element-1582" data-mp="2py4yirl66fp3">
                    240-575-5852
                  </a>{' '}
                  or{' '}
                  <a href="tel:301-620-7585" className="font-semibold text-brand-maroon hover:underline" data-id="element-1583" data-mp="3im0pdfo25ddy">
                    301-620-7585
                  </a>
                  , and email{' '}
                  <a href="mailto:customerservice@midatlanticmailboxes.com" className="font-semibold text-brand-maroon hover:underline break-all" data-id="element-1584" data-mp="16zp3hi0xime0">
                    customerservice@midatlanticmailboxes.com
                  </a>{' '}
                  to obtain a return authorization number (RGA#). Orders
                  returned or discarded without authorization will NOT be
                  processed for a refund.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Return Policy */}
        <section className="py-12 bg-white border-b border-brand-border" data-id="element-1585" data-mp="xz0r0rr6ou38">
          <div className="max-w-5xl mx-auto px-4" data-id="element-1586" data-mp="2dfst23envl47">
            <div className="flex items-center gap-4 mb-6" data-id="element-1587" data-mp="2dinbjpe46vuz">
              <div className="w-12 h-12 rounded-full bg-brand-goldWash text-brand-maroon flex items-center justify-center" data-id="element-1588" data-mp="29dxie8aubvdr">
                <RotateCcw size={24} data-id="element-1589" data-mp="34qylc5zhjea4" />
              </div>
              <h2 className="text-2xl lg:text-3xl font-serif font-bold text-brand-charcoal" data-id="element-1590" data-mp="38pz11fmo20sw">
                Return Policy
              </h2>
            </div>
            <ul className="space-y-3 text-brand-muted leading-relaxed" data-id="element-1591" data-mp="3ds6jmo204f47">
              <li className="flex items-start gap-3" data-id="element-1592" data-mp="3iqhrwucnehkb">
                <CheckCircle2 size={18} className="text-brand-maroon shrink-0 mt-1" data-id="element-1593" data-mp="3nojbrhhwekwu" />
                <span data-id="element-1594" data-mp="3roa77yiddp7p">
                  Returns must be made within{' '}
                  <strong data-id="element-1595" data-mp="anqip4suhwy">30 days from receipt</strong> of your order, in new
                  condition and the original packaging.
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-1596" data-mp="a93ewkcfzd2r">
                <CheckCircle2 size={18} className="text-brand-maroon shrink-0 mt-1" data-id="element-1597" data-mp="aaoeiodm5m7h" />
                <span data-id="element-1598" data-mp="1zq8uihotuq71">
                  Many of our manufacturers may or may not allow a return if the
                  item is not defective.
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-1599" data-mp="1utidns577qkk">
                <CheckCircle2 size={18} className="text-brand-maroon shrink-0 mt-1" data-id="element-1600" data-mp="1pt42w2c7q2iu" />
                <span data-id="element-1601" data-mp="1kv2o3tltkpd6">
                  If approved, a restocking fee applies and varies by
                  manufacturer, with a{' '}
                  <strong data-id="element-1602" data-mp="2dh3v76p6a0zj">minimum restocking fee of 25% or $25</strong>.
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-1603" data-mp="28q5e44wd0yv8">
                <CheckCircle2 size={18} className="text-brand-maroon shrink-0 mt-1" data-id="element-1604" data-mp="28okhm9qhj5uq" />
                <span data-id="element-1605" data-mp="317hnmybo61un">
                  Return shipping charges are the customer's responsibility, and
                  original shipping charges are non-refundable.
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-1606" data-mp="uk0i7vz1cnbn">
                <CheckCircle2 size={18} className="text-brand-maroon shrink-0 mt-1" data-id="element-1607" data-mp="ulboq3jv896g" />
                <span data-id="element-1608" data-mp="ktxp7vrwz9db">
                  Contact us at{' '}
                  <a href="tel:800-806-7997" className="text-brand-maroon font-semibold hover:underline" data-id="element-1609" data-mp="ps926fdi9zzr">
                    800-806-7997
                  </a>{' '}
                  to determine your return requirements. Products may be
                  returned via UPS Ground, FedEx Ground, or USPS.
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-1610" data-mp="6td9x9l7jvok">
                <CheckCircle2 size={18} className="text-brand-maroon shrink-0 mt-1" data-id="element-1611" data-mp="6twrgp6y4i22" />
                <span data-id="element-1612" data-mp="217e6fntac6lh">
                  Products that have been installed or customized are not
                  returnable.
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-1613" data-mp="3lpyj3dz1alxm">
                <CheckCircle2 size={18} className="text-brand-maroon shrink-0 mt-1" data-id="element-1614" data-mp="3gqvl2dhh1fz5" />
                <span data-id="element-1615" data-mp="3vgt1u8d2n3eb">
                  A credit will be issued upon return after inspection, minus
                  the restocking fee and any original shipping fees.
                </span>
              </li>
            </ul>
          </div>
        </section>

        {/* Damage & Shipping Claims */}
        <section className="py-12 bg-brand-background border-b border-brand-border" data-id="element-1616" data-mp="3axkjyn0wrn4">
          <div className="max-w-5xl mx-auto px-4" data-id="element-1617" data-mp="i7eymxmlkkdd">
            <div className="flex items-center gap-4 mb-6" data-id="element-1618" data-mp="d9n4dwyhcm60">
              <div className="w-12 h-12 rounded-full bg-white text-brand-maroon flex items-center justify-center shadow-sm" data-id="element-1619" data-mp="da6cyn28py7d">
                <AlertTriangle size={24} data-id="element-1620" data-mp="2k1xp4rxzjc0e" />
              </div>
              <h2 className="text-2xl lg:text-3xl font-serif font-bold text-brand-charcoal" data-id="element-1621" data-mp="2f2baqf6f7pq0">
                Damage & Shipping Claims
              </h2>
            </div>
            <div className="bg-white border border-brand-border rounded-xl p-6 shadow-sm" data-id="element-1622" data-mp="1y6e0pj2s2xh9">
              <p className="text-brand-charcoal font-semibold mb-4" data-id="element-1623" data-mp="22x42kmbzwi77">
                Please check your order upon delivery. All items must be
                inspected for concealed damage or shortage within{' '}
                <span className="text-brand-maroon" data-id="element-1624" data-mp="1a9rtneyrc8wd">3 days of delivery</span> to
                receive credit.
              </p>
              <ul className="space-y-3 text-brand-muted leading-relaxed text-sm" data-id="element-1625" data-mp="1f8l40yiqzjsb">
                <li className="flex items-start gap-3" data-id="element-1626" data-mp="1k6cx3vv4pby3">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-maroon mt-2 shrink-0" data-id="element-1627" data-mp="1o8rcu73i3vl5"></span>
                  <span data-id="element-1628" data-mp="2vmbbmf92sq6p">
                    Report any damages, manufacturer defects, or shipping errors
                    within <strong data-id="element-1629" data-mp="2zndct1mjzcbb">3 business days</strong> from receipt by
                    calling{' '}
                    <a href="tel:1-800-806-7997" className="text-brand-maroon font-semibold hover:underline" data-id="element-1630" data-mp="o12rk075tuoz">
                      1-800-806-7997
                    </a>
                    .
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-1631" data-mp="a9oaw00q65l3">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-maroon mt-2 shrink-0" data-id="element-1632" data-mp="a721rvy6ri0r"></span>
                  <span data-id="element-1633" data-mp="57yxwetxzhud">
                    Products damaged in shipping must be left in the box as
                    received with all original packing materials.
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-1634" data-mp="3sgm395pfqlsr">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-maroon mt-2 shrink-0" data-id="element-1635" data-mp="3ngzq0w9lx5jy"></span>
                  <span data-id="element-1636" data-mp="1qm3djdlvfy6s">
                    Images of damage may be requested for carrier claims.
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-1637" data-mp="1lmgv8pr6rrr4">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-maroon mt-2 shrink-0" data-id="element-1638" data-mp="2a6ccpowngblf"></span>
                  <span data-id="element-1639" data-mp="257srxrtsl5un">
                    Do not discard any item — a representative from our shipping
                    company may need to inspect the damaged units. Orders
                    returned or discarded without authorization will NOT be
                    refunded.
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Custom Orders */}
        <section className="py-12 bg-white border-b border-brand-border" data-id="element-1640" data-mp="1mg0040qglqoi">
          <div className="max-w-5xl mx-auto px-4" data-id="element-1641" data-mp="20b2d17tq4gat">
            <div className="flex items-center gap-4 mb-6" data-id="element-1642" data-mp="1vj12bqc7exmw">
              <div className="w-12 h-12 rounded-full bg-brand-goldWash text-brand-maroon flex items-center justify-center" data-id="element-1643" data-mp="1vhga47dd3qs7">
                <Sparkles size={24} data-id="element-1644" data-mp="12u3mruzdnexu" />
              </div>
              <h2 className="text-2xl lg:text-3xl font-serif font-bold text-brand-charcoal" data-id="element-1645" data-mp="12tkfd87cs8y2">
                Custom Orders & Exceptions
              </h2>
            </div>
            <div className="space-y-4 text-brand-muted leading-relaxed" data-id="element-1646" data-mp="3j8fe20lchr1r">
              <p data-id="element-1647" data-mp="35gr0dsvg6uio">
                Custom-made items or special orders — such as address plaques,
                personalized mailboxes, or any engraved or personalized items —
                are non-refundable or returnable unless they are damaged during
                shipping or defective from the manufacturer.
              </p>
              <p data-id="element-1648" data-mp="60m4md9d2mxm">
                If an error occurs in customization due to a manufacturer error
                or an error made by MidAtlantic Mailbox, you have the option of
                either a refund or to have it remade.{' '}
                <strong className="text-brand-charcoal" data-id="element-1649" data-mp="3t8g03ualml9n">
                  For custom orders, it is extremely important to double-check
                  your entries.
                </strong>
              </p>
              <p data-id="element-1650" data-mp="2hottmg6uusof">
                America's Finest mailboxes and lanterns, as well as Bacova
                mailboxes, are made to order by hand and are non-returnable. Any
                product that has been assembled or modified is non-returnable.
              </p>
            </div>
          </div>
        </section>

        {/* Cancellation */}
        <section className="py-12 bg-brand-background border-b border-brand-border" data-id="element-1651" data-mp="1jwm3spvb5ami">
          <div className="max-w-5xl mx-auto px-4" data-id="element-1652" data-mp="1eyuabrf0lgg7">
            <div className="flex items-center gap-4 mb-6" data-id="element-1653" data-mp="1ewhjspv6xxrj">
              <div className="w-12 h-12 rounded-full bg-white text-brand-maroon flex items-center justify-center shadow-sm" data-id="element-1654" data-mp="19yfyrze7fodf">
                <XCircle size={24} data-id="element-1655" data-mp="15y5ucr62960o" />
              </div>
              <h2 className="text-2xl lg:text-3xl font-serif font-bold text-brand-charcoal" data-id="element-1656" data-mp="10zku4rqt6mc8">
                Order Cancellation Policy
              </h2>
            </div>
            <p className="text-brand-muted leading-relaxed" data-id="element-1657" data-mp="2vcu7iumh6rzr">
              Many of our products ship within <strong data-id="element-1658" data-mp="2vazhx309ebpc">24–48 hours</strong> of
              processing your order. If you find it necessary to cancel an
              order, please notify us immediately at{' '}
              <a href="mailto:customerservice@midatlanticmailboxes.com" className="text-brand-maroon font-semibold hover:underline break-all" data-id="element-1659" data-mp="37tldrfvq5fgg">
                customerservice@midatlanticmailboxes.com
              </a>
              . If an order has already shipped, it will be your responsibility
              to pay to ship the item back, and restocking fees may apply.
            </p>
          </div>
        </section>

        {/* Shipping */}
        <section className="py-12 bg-white border-b border-brand-border" data-id="element-1660" data-mp="2tuddskrqm3b7">
          <div className="max-w-5xl mx-auto px-4" data-id="element-1661" data-mp="2xybhl8s0c2al">
            <div className="flex items-center gap-4 mb-6" data-id="element-1662" data-mp="95fy1sjuaxdj">
              <div className="w-12 h-12 rounded-full bg-brand-goldWash text-brand-maroon flex items-center justify-center" data-id="element-1663" data-mp="94wds56pvwwc">
                <Truck size={24} data-id="element-1664" data-mp="d942w3h37ev3" />
              </div>
              <h2 className="text-2xl lg:text-3xl font-serif font-bold text-brand-charcoal" data-id="element-1665" data-mp="bqx4lwq5xpjm">
                Shipping Policy
              </h2>
            </div>
            <div className="space-y-4 text-brand-muted leading-relaxed" data-id="element-1666" data-mp="lo1ua6j7w356">
              <p data-id="element-1667" data-mp="llysyqcm6nl5">
                Please check your order upon delivery. All items must be
                inspected for concealed damage or shortage within 3 days of
                delivery to receive credit.
              </p>
              <p data-id="element-1668" data-mp="ppwsv39rk2f9">
                Shipping costs vary for each order and are determined by the
                size and weight of the order and the distance between the two
                ZIP codes. We often arrange to have a product shipped directly
                from the manufacturer, which keeps overhead costs down so we can
                pass those savings on to you.
              </p>
              <p data-id="element-1669" data-mp="19dma4gk2qka8">
                If you need your order expedited, please call us to arrange a
                rushed order. Some larger items, such as CBUs or bulk orders,
                will ship via Freight.
              </p>
            </div>
          </div>
        </section>

        {/* Warranty & Privacy */}
        <section className="py-12 bg-brand-background border-b border-brand-border" data-id="element-1670" data-mp="1j1rp8mfdrtqd">
          <div className="max-w-5xl mx-auto px-4 grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1671" data-mp="1o02wcpeajq4w">
            <div className="bg-white border border-brand-border rounded-xl p-6 shadow-sm" data-id="element-1672" data-mp="19bf52vbha2kr">
              <div className="flex items-center gap-3 mb-4" data-id="element-1673" data-mp="199lw39de769l">
                <div className="w-11 h-11 rounded-full bg-brand-goldWash text-brand-maroon flex items-center justify-center" data-id="element-1674" data-mp="1e2x43dtxxt33">
                  <ShieldCheck size={22} data-id="element-1675" data-mp="108nt8ueox4sl" />
                </div>
                <h2 className="text-xl font-serif font-bold text-brand-charcoal" data-id="element-1676" data-mp="14d58qhe0ovxk">
                  Warranty
                </h2>
              </div>
              <p className="text-brand-muted text-sm leading-relaxed" data-id="element-1677" data-mp="vbnvr7vwpad3">
                One-year product and installation warranty from the date of
                service or purchase, per manufacturer guidelines. Warranties do
                not include damage from acts of God (wind, water, or snow) or
                third-party damage such as vandalism or vehicle damage from any
                moving vehicle, package delivery, snow plow, or paving
                equipment.
              </p>
            </div>
            <div className="bg-white border border-brand-border rounded-xl p-6 shadow-sm" data-id="element-1678" data-mp="3g07dvwdnamuc">
              <div className="flex items-center gap-3 mb-4" data-id="element-1679" data-mp="3bydhi0ahg4zz">
                <div className="w-11 h-11 rounded-full bg-brand-goldWash text-brand-maroon flex items-center justify-center" data-id="element-1680" data-mp="3byvifa83b7e8">
                  <Lock size={22} data-id="element-1681" data-mp="31zwl2nsryy6j" />
                </div>
                <h2 className="text-xl font-serif font-bold text-brand-charcoal" data-id="element-1682" data-mp="31zegn3vyldnj">
                  Privacy Statement
                </h2>
              </div>
              <p className="text-brand-muted text-sm leading-relaxed" data-id="element-1683" data-mp="1qk7zo0v6677u">
                We respect your privacy. Any and all information collected on
                our website will be kept strictly confidential and will not be
                sold, reused, disclosed, or loaned out.
              </p>
            </div>
          </div>
        </section>

        {/* Customer Service CTA */}
        <section className="py-16 lg:py-20 bg-brand-charcoal text-center" data-id="element-1684" data-mp="1v5gohcozrc92">
          <div className="max-w-3xl mx-auto px-4" data-id="element-1685" data-mp="1v7tbw5diqeti">
            <h2 className="text-3xl font-serif font-bold text-white mb-4" data-id="element-1686" data-mp="1z6ace62for1y">
              Questions About a Policy?
            </h2>
            <p className="text-gray-300 mb-8" data-id="element-1687" data-mp="244k9im7py1q7">
              Our customer service team is here to help with returns, claims,
              and order questions.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-1688" data-mp="2e39s6h7fvq7i">
              <a href="tel:301-620-7585" className="bg-brand-gold hover:bg-brand-gold/90 text-brand-charcoal px-8 py-3.5 rounded-md font-bold transition-colors shadow-sm w-full sm:w-auto flex items-center justify-center gap-2" data-id="element-1689" data-mp="esid990347m0">
                <Phone size={18} data-id="element-1690" data-mp="2gpiv3yf6ok4x" /> Call 301-620-7585
              </a>
              <a href="mailto:customerservice@midatlanticmailboxes.com" className="bg-transparent border-2 border-white hover:bg-white hover:text-brand-charcoal text-white px-8 py-3.5 rounded-md font-bold transition-colors shadow-sm w-full sm:w-auto flex items-center justify-center gap-2" data-id="element-1691" data-mp="2gn6eppp2qhtz">
                <Mail size={18} data-id="element-1692" data-mp="1xqy7igci3ww1" /> Email Customer Service
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer data-id="element-1693" data-mp="2brh8x4mgo4p8" />
      <MobileStickyBar data-id="element-1694" data-mp="2bnti4d8ehwe7" />
    </div>;
}